import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import useNotificationProvider from 'hooks/notificationBar/useNotificationProvider';
import {
  getBoards,
  getBoard,
  getWorkItems,
  createBoard as apiCreateBoard,
  updateBoard as apiUpdateBoard,
  deleteBoard as apiDeleteBoard,
  createColumn as apiCreateColumn,
  updateColumn as apiUpdateColumn,
  reorderColumns as apiReorderColumns,
  deleteColumn as apiDeleteColumn,
  createWorkItem as apiCreateWorkItem,
  updateWorkItem as apiUpdateWorkItem,
  moveWorkItem as apiMoveWorkItem,
  deleteWorkItem as apiDeleteWorkItem,
  assignUser as apiAssignUser,
  removeAssignee as apiRemoveAssignee,
  importWorkItem as apiImportWorkItem,
  completeWorkItem as apiCompleteWorkItem,
} from 'api/todo/todoApi';
import { BoardWithCreator, BoardWithColumns, WorkItem, Column, WorkItemFilters, ChecklistItem } from 'api/todo/types';
import { getTodoErrorMessage } from '../utils/errorMessages';

interface UseTodoDataReturn {
  // Board list
  boards: BoardWithCreator[];
  boardsLoading: boolean;

  // Current board
  currentBoard: BoardWithColumns | null;
  workItems: WorkItem[];
  boardLoading: boolean;
  isOwner: boolean;

  // Actions
  selectBoard: (boardId: number) => void;
  createBoard: (name: string, isPrivate: boolean) => Promise<void>;
  updateBoard: (name: string, isPrivate: boolean) => Promise<void>;
  deleteBoard: () => Promise<void>;

  // Column actions
  createColumn: (name: string) => Promise<void>;
  updateColumn: (columnId: number, name: string) => Promise<void>;
  reorderColumns: (columnOrder: number[]) => Promise<void>;
  deleteColumn: (columnId: number) => Promise<void>;

  // Work item actions
  createTask: (columnId: number, title: string, description?: string, caseId?: number, caseTitle?: string, dateDue?: string | null, checklist?: ChecklistItem[] | null) => Promise<WorkItem | undefined>;
  updateTask: (workItemId: number, title: string, description?: string, caseId?: number | null, caseTitle?: string | null, dateDue?: string | null, checklist?: ChecklistItem[] | null) => Promise<void>;
  moveTask: (workItemId: number, columnId: number, sortOrder: number) => Promise<void>;
  deleteTask: (workItemId: number) => Promise<void>;
  assignUser: (workItemId: number, userId: number) => Promise<void>;
  removeAssignee: (workItemId: number, userId: number) => Promise<void>;

  // Linking actions
  importTask: (workItemId: number, targetBoardId: number, targetColumnId: number) => Promise<void>;
  completeTask: (workItemId: number) => Promise<void>;

  // Filters
  filters: WorkItemFilters;
  setFilters: (filters: WorkItemFilters) => void;
  availableCases: { case_id: number; case_title: string }[];

  // Pagination
  columnTotals: Record<number, number>;
  loadMore: () => void;

  // Refresh
  refreshBoards: () => Promise<void>;
  refreshBoard: () => Promise<void>;
}

export const useTodoData = (): UseTodoDataReturn => {
  const { userInfo } = useSelector(selectUserInfo);
  const { notifyError, notifySuccess } = useNotificationProvider();

  const [boards, setBoards] = useState<BoardWithCreator[]>([]);
  const [boardsLoading, setBoardsLoading] = useState<boolean>(true);
  const [currentBoardId, setCurrentBoardId] = useState<number | null>(null);
  const [currentBoard, setCurrentBoard] = useState<BoardWithColumns | null>(null);
  const [workItems, setWorkItems] = useState<WorkItem[]>([]);
  const [boardLoading, setBoardLoading] = useState<boolean>(false);
  const [filters, setFilters] = useState<WorkItemFilters>({});
  const [availableCases, setAvailableCases] = useState<{ case_id: number; case_title: string }[]>([]);
  const [perColumnLimit, setPerColumnLimit] = useState<number>(15);
  const [columnTotals, setColumnTotals] = useState<Record<number, number>>({});

  const mountedRef = useRef(true);

  const isOwner = currentBoard
    ? currentBoard.creator_user_id === parseInt(userInfo.userId || '0')
    : false;

  // Fetch boards list
  const fetchBoards = useCallback(async () => {
    try {
      setBoardsLoading(true);
      const data = await getBoards();
      if (mountedRef.current) {
        // Ensure data is always an array
        const boardsArray = Array.isArray(data) ? data : [];
        setBoards(boardsArray);
        // Auto-select first board if none selected
        if (!currentBoardId && boardsArray.length > 0) {
          setCurrentBoardId(boardsArray[0].board_id);
        }
      }
    } catch (error) {
      console.error('[useTodoData] Failed to fetch boards:', error);
      if (mountedRef.current) {
        // Set empty array on error to prevent map errors
        setBoards([]);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
      }
    } finally {
      if (mountedRef.current) {
        setBoardsLoading(false);
      }
    }
  }, [currentBoardId, notifyError]);

  // Fetch current board and work items
  const fetchBoard = useCallback(async () => {
    if (!currentBoardId) {
      setCurrentBoard(null);
      setWorkItems([]);
      return;
    }

    try {
      setBoardLoading(true);
      const [boardData, workItemsResult] = await Promise.all([
        getBoard(currentBoardId),
        getWorkItems(currentBoardId, filters, perColumnLimit),
      ]);

      if (mountedRef.current) {
        // Handle null board response (board might not exist or was deleted)
        setCurrentBoard(boardData || null);
        // Ensure workItems is always an array
        const items = Array.isArray(workItemsResult.items) ? workItemsResult.items : [];
        setWorkItems(items);
        setColumnTotals(workItemsResult.columnTotals || {});

        // Cache available cases on unfiltered loads
        if (!filters.assigneeIds?.length && !filters.caseIds?.length) {
          const casesMap = new Map<number, string>();
          items.forEach((item) => {
            if (item.case_id && item.case_title) {
              casesMap.set(item.case_id, item.case_title);
            }
          });
          setAvailableCases(
            Array.from(casesMap.entries()).map(([case_id, case_title]) => ({ case_id, case_title }))
          );
        }
      }
    } catch (error) {
      console.error('[useTodoData] Failed to fetch board:', error);
      if (mountedRef.current) {
        // Clear board state on error to prevent stale data
        setCurrentBoard(null);
        setWorkItems([]);
        setColumnTotals({});
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
      }
    } finally {
      if (mountedRef.current) {
        setBoardLoading(false);
      }
    }
  }, [currentBoardId, filters, perColumnLimit, notifyError]);

  // Initial load
  useEffect(() => {
    fetchBoards();
  }, [fetchBoards]);

  // Fetch board when selected
  useEffect(() => {
    fetchBoard();
  }, [fetchBoard]);

  // Cleanup
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selectBoard = useCallback((boardId: number) => {
    setCurrentBoardId(boardId);
    setFilters({});
    setPerColumnLimit(15);
  }, []);

  const handleSetFilters = useCallback((newFilters: WorkItemFilters) => {
    setFilters(newFilters);
    setPerColumnLimit(15);
  }, []);

  const loadMore = useCallback(() => {
    setPerColumnLimit((prev) => prev + 15);
  }, []);

  const createBoard = useCallback(
    async (name: string, isPrivate: boolean) => {
      try {
        const newBoard = await apiCreateBoard(name, isPrivate);
        if (mountedRef.current) {
          await fetchBoards();
          setCurrentBoardId(newBoard.board_id);
          notifySuccess('Board created successfully');
        }
      } catch (error) {
        console.error('[useTodoData] Failed to create board:', error);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [fetchBoards, notifyError, notifySuccess]
  );

  const updateBoard = useCallback(
    async (name: string, isPrivate: boolean) => {
      if (!currentBoardId) return;

      try {
        await apiUpdateBoard(currentBoardId, name, isPrivate);
        if (mountedRef.current) {
          await Promise.all([fetchBoards(), fetchBoard()]);
          notifySuccess('Board updated successfully');
        }
      } catch (error) {
        console.error('[useTodoData] Failed to update board:', error);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [currentBoardId, fetchBoards, fetchBoard, notifyError, notifySuccess]
  );

  const deleteBoard = useCallback(async () => {
    if (!currentBoardId) return;

    try {
      await apiDeleteBoard(currentBoardId);
      if (mountedRef.current) {
        await fetchBoards();
        setCurrentBoardId(null);
        setCurrentBoard(null);
        setWorkItems([]);
        notifySuccess('Board deleted successfully');
      }
    } catch (error) {
      console.error('[useTodoData] Failed to delete board:', error);
      const errorCode = (error as Error & { code?: string })?.code;
      notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
      throw error;
    }
  }, [currentBoardId, fetchBoards, notifyError, notifySuccess]);

  const createColumn = useCallback(
    async (name: string) => {
      if (!currentBoardId) return;

      try {
        await apiCreateColumn(currentBoardId, name);
        if (mountedRef.current) {
          await fetchBoard();
          notifySuccess('Column created successfully');
        }
      } catch (error) {
        console.error('[useTodoData] Failed to create column:', error);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [currentBoardId, fetchBoard, notifyError, notifySuccess]
  );

  const updateColumn = useCallback(
    async (columnId: number, name: string) => {
      try {
        await apiUpdateColumn(columnId, name);
        if (mountedRef.current) {
          await fetchBoard();
          notifySuccess('Column updated successfully');
        }
      } catch (error) {
        console.error('[useTodoData] Failed to update column:', error);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [fetchBoard, notifyError, notifySuccess]
  );

  const reorderColumns = useCallback(
    async (columnOrder: number[]) => {
      if (!currentBoardId || !currentBoard) return;

      // Save previous state for rollback
      const previousBoard = currentBoard;

      try {
        // Optimistic update - reorder columns and update sort_order values
        const reorderedColumns = columnOrder.map((colId, index) => {
          const column = currentBoard.columns.find((c) => c.column_id === colId);
          return column ? { ...column, sort_order: index } : null;
        }).filter((c): c is Column => c !== null);

        setCurrentBoard({ ...currentBoard, columns: reorderedColumns });

        await apiReorderColumns(currentBoardId, columnOrder);
        // Success - optimistic state is correct, no refetch needed
      } catch (error) {
        console.error('[useTodoData] Failed to reorder columns:', error);
        // Rollback to previous state
        if (mountedRef.current) {
          setCurrentBoard(previousBoard);
        }
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [currentBoardId, currentBoard, notifyError]
  );

  const deleteColumn = useCallback(
    async (columnId: number) => {
      try {
        await apiDeleteColumn(columnId);
        if (mountedRef.current) {
          await fetchBoard();
          notifySuccess('Column deleted successfully');
        }
      } catch (error) {
        console.error('[useTodoData] Failed to delete column:', error);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [fetchBoard, notifyError, notifySuccess]
  );

  const createTask = useCallback(
    async (columnId: number, title: string, description?: string, caseId?: number, caseTitle?: string, dateDue?: string | null, checklist?: ChecklistItem[] | null): Promise<WorkItem | undefined> => {
      if (!currentBoardId) return undefined;

      try {
        const newTask = await apiCreateWorkItem(currentBoardId, columnId, title, description, caseId, caseTitle, dateDue, checklist);
        if (mountedRef.current) {
          // Add the new task at top — mirror server's sort_order shift
          setWorkItems((prev) => [
            ...prev.map((t) => t.column_id === columnId ? { ...t, sort_order: t.sort_order + 1 } : t),
            newTask,
          ]);
          setColumnTotals((prev) => ({ ...prev, [columnId]: (prev[columnId] || 0) + 1 }));
          notifySuccess('Task created successfully');
        }
        return newTask;
      } catch (error) {
        console.error('[useTodoData] Failed to create task:', error);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [currentBoardId, notifyError, notifySuccess]
  );

  const updateTask = useCallback(
    async (workItemId: number, title: string, description?: string, caseId?: number | null, caseTitle?: string | null, dateDue?: string | null, checklist?: ChecklistItem[] | null) => {
      // Save previous state for rollback
      const previousWorkItems = workItems;

      try {
        // Optimistic update
        setWorkItems((prev) =>
          prev.map((t) =>
            t.work_item_id === workItemId
              ? {
                  ...t,
                  title,
                  description: description || null,
                  case_id: caseId ?? t.case_id,
                  case_title: caseTitle ?? t.case_title,
                  date_due: dateDue !== undefined ? dateDue : t.date_due,
                  checklist: checklist !== undefined ? checklist : t.checklist,
                }
              : t
          )
        );

        const updatedTask = await apiUpdateWorkItem(workItemId, title, description, caseId, caseTitle, dateDue, checklist);
        if (mountedRef.current) {
          // Update with server response to ensure consistency
          setWorkItems((prev) =>
            prev.map((t) => (t.work_item_id === workItemId ? updatedTask : t))
          );
          notifySuccess('Task updated successfully');
        }
      } catch (error) {
        console.error('[useTodoData] Failed to update task:', error);
        // Rollback to previous state
        if (mountedRef.current) {
          setWorkItems(previousWorkItems);
        }
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [workItems, notifyError, notifySuccess]
  );

  const moveTask = useCallback(
    async (workItemId: number, columnId: number, sortOrder: number) => {
      // Save previous state for rollback
      const previousWorkItems = workItems;
      const previousColumnTotals = columnTotals;

      try {
        // Optimistic update
        const task = workItems.find((t) => t.work_item_id === workItemId);
        if (task) {
          const sourceColumnId = task.column_id;
          const updatedTask = { ...task, column_id: columnId, sort_order: sortOrder };
          setWorkItems((prev) =>
            prev
              .filter((t) => t.work_item_id !== workItemId)
              .concat(updatedTask)
              .sort((a, b) => {
                if (a.column_id !== b.column_id) return a.column_id - b.column_id;
                return a.sort_order - b.sort_order;
              })
          );
          if (sourceColumnId !== columnId) {
            setColumnTotals((prev) => ({
              ...prev,
              [sourceColumnId]: Math.max((prev[sourceColumnId] || 0) - 1, 0),
              [columnId]: (prev[columnId] || 0) + 1,
            }));
          }
        }

        const movedTask = await apiMoveWorkItem(workItemId, columnId, sortOrder);
        if (mountedRef.current) {
          // Update with server response to ensure consistency
          setWorkItems((prev) =>
            prev.map((t) => (t.work_item_id === workItemId ? movedTask : t))
          );
        }
        // Success - no fetchBoard needed
      } catch (error) {
        console.error('[useTodoData] Failed to move task:', error);
        // Rollback to previous state
        if (mountedRef.current) {
          setWorkItems(previousWorkItems);
          setColumnTotals(previousColumnTotals);
        }
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [workItems, columnTotals, notifyError]
  );

  const deleteTask = useCallback(
    async (workItemId: number) => {
      // Save previous state for rollback
      const previousWorkItems = workItems;
      const previousColumnTotals = columnTotals;

      try {
        // Optimistic update - remove task immediately
        const deletedTask = workItems.find((t) => t.work_item_id === workItemId);
        setWorkItems((prev) => prev.filter((t) => t.work_item_id !== workItemId));
        if (deletedTask) {
          setColumnTotals((prev) => ({
            ...prev,
            [deletedTask.column_id]: Math.max((prev[deletedTask.column_id] || 0) - 1, 0),
          }));
        }

        await apiDeleteWorkItem(workItemId);
        if (mountedRef.current) {
          notifySuccess('Task deleted successfully');
        }
        // Success - no fetchBoard needed
      } catch (error) {
        console.error('[useTodoData] Failed to delete task:', error);
        // Rollback to previous state
        if (mountedRef.current) {
          setWorkItems(previousWorkItems);
          setColumnTotals(previousColumnTotals);
        }
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [workItems, columnTotals, notifyError, notifySuccess]
  );

  const assignUser = useCallback(
    async (workItemId: number, userId: number) => {
      // Save previous state for rollback
      const previousWorkItems = workItems;

      try {
        const updatedTask = await apiAssignUser(workItemId, userId);
        if (mountedRef.current) {
          // Update with server response to get the full assignee data
          setWorkItems((prev) =>
            prev.map((t) => (t.work_item_id === workItemId ? updatedTask : t))
          );
          notifySuccess('User assigned successfully');
        }
        // Success - no fetchBoard needed
      } catch (error) {
        console.error('[useTodoData] Failed to assign user:', error);
        // Rollback to previous state
        if (mountedRef.current) {
          setWorkItems(previousWorkItems);
        }
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [workItems, notifyError, notifySuccess]
  );

  const removeAssignee = useCallback(
    async (workItemId: number, userId: number) => {
      // Save previous state for rollback
      const previousWorkItems = workItems;

      try {
        // Optimistic update - remove assignee immediately
        setWorkItems((prev) =>
          prev.map((t) =>
            t.work_item_id === workItemId
              ? { ...t, assignees: t.assignees.filter((a) => a.user_id !== userId) }
              : t
          )
        );

        const updatedTask = await apiRemoveAssignee(workItemId, userId);
        if (mountedRef.current) {
          // Update with server response to ensure consistency
          setWorkItems((prev) =>
            prev.map((t) => (t.work_item_id === workItemId ? updatedTask : t))
          );
          notifySuccess('Assignee removed successfully');
        }
        // Success - no fetchBoard needed
      } catch (error) {
        console.error('[useTodoData] Failed to remove assignee:', error);
        // Rollback to previous state
        if (mountedRef.current) {
          setWorkItems(previousWorkItems);
        }
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [workItems, notifyError, notifySuccess]
  );

  const importTask = useCallback(
    async (workItemId: number, targetBoardId: number, targetColumnId: number) => {
      try {
        await apiImportWorkItem(workItemId, targetBoardId, targetColumnId);
        if (mountedRef.current) {
          notifySuccess('Task imported to board successfully');
        }
      } catch (error) {
        console.error('[useTodoData] Failed to import task:', error);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [notifyError, notifySuccess]
  );

  const completeTask = useCallback(
    async (workItemId: number) => {
      try {
        await apiCompleteWorkItem(workItemId);
        if (mountedRef.current) {
          // Refresh current board to reflect the moved items
          await fetchBoard();
          notifySuccess('Task marked as complete');
        }
      } catch (error) {
        console.error('[useTodoData] Failed to complete task:', error);
        const errorCode = (error as Error & { code?: string })?.code;
        notifyError(getTodoErrorMessage(errorCode || 'SERVER_ERROR'));
        throw error;
      }
    },
    [fetchBoard, notifyError, notifySuccess]
  );

  return {
    boards,
    boardsLoading,
    currentBoard,
    workItems,
    boardLoading,
    isOwner,
    selectBoard,
    createBoard,
    updateBoard,
    deleteBoard,
    createColumn,
    updateColumn,
    reorderColumns,
    deleteColumn,
    createTask,
    updateTask,
    moveTask,
    deleteTask,
    assignUser,
    removeAssignee,
    importTask,
    completeTask,
    filters,
    setFilters: handleSetFilters,
    availableCases,
    columnTotals,
    loadMore,
    refreshBoards: fetchBoards,
    refreshBoard: fetchBoard,
  };
};
